import React, { useState, useEffect } from 'react';
import authFetch from '../api';
import { BarChart } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AdminAnalytics = () => {
  const { t } = useTranslation();
  
  const PROJECT_TABS = [
    { key: 'overview', label: t('adminAnalytics.overview') },
    { key: 'activities', label: t('adminAnalytics.activities') },
    { key: 'comparisons', label: t('adminAnalytics.comparisons') },
    { key: 'insights', label: t('adminAnalytics.insights') },
    { key: 'performance', label: t('adminAnalytics.performance') },
    { key: 'worklogs', label: t('adminAnalytics.workLogs') },
  ];

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setError(null);
        console.log('🔍 Fetching projects for analytics...');
        
        const res = await authFetch('/projects/assigned');
        
        let projectsData;
        if (res && res.data) {
          projectsData = res.data;
        } else {
          projectsData = res;
        }
        
        console.log('📊 Projects data received:', projectsData);
        const projectsArray = Array.isArray(projectsData) ? projectsData : [];
        setProjects(projectsArray);
        
        if (projectsArray.length > 0) {
          setSelectedProjectId(projectsArray[0]._id);
        }
        
      } catch (err) {
        console.error('❌ Failed to fetch projects:', err);
        console.error('Error details:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data
        });
        setError(t('adminAnalytics.failedToLoadProjects'));
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const selectedProject = projects.find(p => p._id === selectedProjectId);

  // Helpers for stats
  const getOverallProgress = (project) => {
    if (!project) return '0.0';
    
    // Use percentCompleted if available, otherwise calculate from activities
    if (project.percentCompleted !== undefined) {
      return project.percentCompleted.toFixed(1);
    }
    
    const activities = project.activities || [];
    if (activities.length === 0) return '0.0';
    
    const total = activities.reduce((sum, act) => sum + (act.planned || 0), 0);
    const completed = activities.reduce((sum, act) => sum + (act.completed || 0), 0);
    
    return total > 0 ? ((completed / total) * 100).toFixed(1) : '0.0';
  };

  const getActivityCount = (project) =>
    project?.activities?.length || 0;

  const getAssignedWorkers = (project) =>
    project?.assignedEmployees?.length || 0;

  const getLoggedWorkers = (project) => {
    const workerSet = new Set();
    (project?.activities || []).forEach(a => {
      (a.dailyLog || []).forEach(log => {
        if (log.by) {
          if (typeof log.by === 'object' && log.by.name) {
            workerSet.add(log.by.name);
          } else if (typeof log.by === 'string') {
            workerSet.add(log.by);
          }
        }
      });
    });
    return workerSet.size;
  };

  // Debug information
  console.log('🔍 AdminAnalytics Debug:', {
    projectsCount: projects.length,
    selectedProjectId,
    selectedProject: selectedProject?.name,
    activeTab,
    loading,
    error
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('adminAnalytics.analyticsDashboard')}</h1>
          <p className="text-gray-600 mt-2">{t('adminAnalytics.monitorProjectPerformance')}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        <div className="flex flex-col md:flex-row">
          {/* Sidebar */}
          <div className="w-full md:w-1/4 border-r border-gray-200">
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('adminAnalytics.projects')}</h2>
              {error ? (
                <div className="text-red-600 text-sm mb-4">{error}</div>
              ) : null}
              <ul className="space-y-2">
                {loading ? (
                  <li className="text-gray-400 text-sm">{t('adminAnalytics.loading')}</li>
                ) : projects.length === 0 ? (
                  <li className="text-gray-400 text-sm">{t('adminAnalytics.noProjectsFound')}</li>
                ) : (
                  projects.map(project => (
                    <li
                      key={project._id}
                      onClick={() => {
                        setSelectedProjectId(project._id);
                        setActiveTab('overview');
                      }}
                      className={`cursor-pointer px-3 py-2 rounded-md text-sm font-medium transition-colors
                        ${selectedProjectId === project._id
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      {project.name}
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6">
            {selectedProject ? (
              <>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">{selectedProject.name}</h2>
                
                {/* Tabs */}
                <div className="border-b border-gray-200 mb-6">
                  <nav className="-mb-px flex space-x-8">
                    {PROJECT_TABS.map(tab => (
                      <button
                        key={tab.key}
                        className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors
                          ${activeTab === tab.key
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        onClick={() => setActiveTab(tab.key)}
                        type="button"
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                      <div className="text-center">
                        <span className="text-3xl font-bold text-blue-600 mb-2 block">
                          {getOverallProgress(selectedProject)}%
                        </span>
                        <span className="text-sm font-medium text-gray-700">{t('adminAnalytics.overallProgress')}</span>
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                      <div className="text-center">
                        <span className="text-3xl font-bold text-green-600 mb-2 block">
                          {getActivityCount(selectedProject)}
                        </span>
                        <span className="text-sm font-medium text-gray-700">{t('adminAnalytics.activities')}</span>
                      </div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
                      <div className="text-center">
                        <span className="text-3xl font-bold text-yellow-600 mb-2 block">
                          {getAssignedWorkers(selectedProject)}
                        </span>
                        <span className="text-sm font-medium text-gray-700">{t('adminAnalytics.assignedWorkers')}</span>
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
                      <div className="text-center">
                        <span className="text-3xl font-bold text-purple-600 mb-2 block">
                          {getLoggedWorkers(selectedProject)}
                        </span>
                        <span className="text-sm font-medium text-gray-700">{t('adminAnalytics.loggedWorkers')}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Other Tabs Placeholder */}
                {activeTab !== 'overview' && (
                  <div className="text-center text-gray-500 py-16">
                    <BarChart className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {PROJECT_TABS.find(t => t.key === activeTab)?.label} {t('adminAnalytics.comingSoon')}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {t('adminAnalytics.featureUnderDevelopment')}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center text-gray-500 py-16">
                <BarChart className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('adminAnalytics.noProjectSelected')}</h3>
                <p className="text-sm text-gray-500">
                  {t('adminAnalytics.selectProjectFromSidebar')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminAnalytics;